import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'
import { useScrollSpy } from '@/hooks/use-scroll-spy'
import { ABOUT_SECTIONS, ABOUT_SECTION_IDS } from '@/constants/about'

/**
 * The spine: a sticky index that marks where you are, with a hairline that
 * fills as the page is read. The fill is written straight to the node so
 * scrolling never re-renders React.
 */
export const IndexRail: FC = (): ReactNode => {
    const active = useScrollSpy(ABOUT_SECTION_IDS)
    const fillRef = useRef<HTMLSpanElement | null>(null)

    useEffect(() => {
        let queued = false
        let raf = 0

        const update = () => {
            queued = false

            const fill = fillRef.current
            if (!fill) return

            const max = document.documentElement.scrollHeight - window.innerHeight
            const progress = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0

            fill.style.height = `${(progress * 100).toFixed(2)}%`
        }

        const onScroll = () => {
            if (queued) return
            queued = true
            raf = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [])

    return (
        <nav aria-label='Sections' className='sticky top-32'>
            <div className='flex gap-5'>
                <span aria-hidden='true' className='relative block w-px shrink-0 bg-fz-rule'>
                    <span
                        ref={fillRef}
                        className='absolute inset-x-0 top-0 block bg-fz-rule-on'
                        style={{ height: '0%' }}
                    />
                </span>

                <ul className='flex flex-col gap-4'>
                    {ABOUT_SECTIONS.map(section => {
                        const isActive = active === section.id

                        return (
                            <li key={section.id}>
                                <a
                                    href={`#${section.id}`}
                                    aria-current={isActive ? 'true' : undefined}
                                    className={cn(
                                        'flex items-center gap-3 text-[11px] uppercase leading-none tracking-[0.24em] transition-colors duration-500',
                                        isActive ? 'text-fz-ink' : 'text-fz-faint hover:text-fz-sub'
                                    )}
                                >
                                    <span
                                        aria-hidden='true'
                                        className={cn(
                                            'block h-px transition-all duration-500',
                                            isActive ? 'w-6 bg-fz-rule-on' : 'w-2.5 bg-fz-rule'
                                        )}
                                    />

                                    {section.label}
                                </a>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </nav>
    )
}
